import React, { useLayoutEffect, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { Assignment, Visibility } from '@mui/icons-material'
import { Droppable, Draggable } from 'react-beautiful-dnd'
import moment from 'moment'
import { CostSourceText } from '../CostSourceText'
import type { QuoteCardWithButtonsProps } from './types'
import {
  LINE_ITEM_TYPE_NAMES,
  LINE_ITEM_TYPE_ORDER,
  STEP2_PT_GRID_COLUMNS,
  STEP2_STANDARD_GRID_COLUMNS,
  SCROLLABLE_MAX_HEIGHT,
} from './constants'
import { calculateTotal, formatCurrency, groupLineItemsByType } from './utils'

const QuoteCardWithButtons = React.memo(function QuoteCardWithButtons({
  quote,
  quoteType,
  availableButtons,
  consumerQuote,
  onToggleButton,
  onAssignAll,
  onAssignAllClaims,
}: QuoteCardWithButtonsProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollTopRef.current
    }
  })

  const groupedItems = useMemo(
    () => groupLineItemsByType(quote.lineItems.filter((item) => !item.originalQuoteId)),
    [quote.lineItems]
  )

  const totalAssignableButtonCount = useMemo(() => {
    const consumerLineItemTypes = new Set(
      consumerQuote.lineItems.map((item) => item.type)
    )

    return availableButtons.filter((btn) => {
      if (btn.originalBundleQuoteId) return false

      if (quoteType === 'original') {
        return btn.lineItemId === null && consumerLineItemTypes.has(btn.type)
      }

      return btn.lineItemId !== null && btn.lineItemId.startsWith(`${quoteType}-buttons-`)
    }).length
  }, [availableButtons, quoteType, consumerQuote.lineItems])

  return (
    <Card elevation={3}>
      <CardHeader
        title={quote.title}
        avatar={<Assignment color="primary" />}
        action={
          <Button
            size="small"
            variant="outlined"
            onClick={() => onAssignAllClaims(quoteType)}
            disabled={totalAssignableButtonCount === 0}
            sx={{ mt: 1, mr: 1 }}
          >
            {quoteType === 'original' ? 'Assign All' : 'Un-assign All'}
          </Button>
        }
        subheader={
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" display="block">
              <strong>Dates:</strong> {moment(quote.startDate).format('L')} -{' '}
              {moment(quote.endDate).format('L')}
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Bundle Duration:</strong> {quote.bundleDurationDays} days
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Group Number:</strong> {quote.groupNumber}
            </Typography>
          </Box>
        }
      />
      <CardContent>
        <Box
          ref={scrollContainerRef}
          onScroll={(e: React.UIEvent<HTMLDivElement>) => {
            scrollTopRef.current = e.currentTarget.scrollTop
          }}
          sx={{ maxHeight: SCROLLABLE_MAX_HEIGHT, overflow: 'auto' }}
        >
          {(() => {
            const hasButtons = availableButtons.some((btn) =>
              quoteType === 'original'
                ? btn.lineItemId === null
                : btn.lineItemId !== null &&
                  btn.lineItemId.startsWith(`${quoteType}-buttons-`)
            )

            const hasConsumerAssignedButtons =
              quoteType === 'original' &&
              availableButtons.some((btn) => btn.lineItemId !== null)

            if (
              quote.lineItems.length === 0 &&
              !hasButtons &&
              !hasConsumerAssignedButtons
            ) {
              return (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1">
                    {quoteType === 'consumer'
                      ? 'No line items in consumer quote'
                      : 'No line items in original quote'}
                  </Typography>
                  {quoteType === 'consumer' && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Go back to Step 1 to add line items
                    </Typography>
                  )}
                </Box>
              )
            }

            return LINE_ITEM_TYPE_ORDER.map((type) => {
              const items = groupedItems[type]
              const droppableId = `${quoteType}-buttons-${type}`

              const sectionButtons = availableButtons.filter(
                (btn) =>
                  btn.type === type &&
                  (quoteType === 'original'
                    ? btn.lineItemId === null
                    : btn.lineItemId === droppableId)
              )
              const movableSectionButtonCount = sectionButtons.filter(
                (btn) => !btn.originalBundleQuoteId
              ).length

              if (quoteType === 'original') {
                const consumerHasTypeItems = consumerQuote.lineItems.some(
                  (item) => item.type === type
                )
                if (!consumerHasTypeItems) return null
              }

              if (items.length === 0 && sectionButtons.length === 0) {
                if (quoteType === 'consumer') return null
                const hasAssignedButtonsOfType = availableButtons.some(
                  (btn) => btn.type === type && btn.lineItemId !== null
                )
                if (!hasAssignedButtonsOfType) return null
              }

              return (
                <Box key={type} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      p: 1,
                      borderRadius: 1,
                      mb: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {LINE_ITEM_TYPE_NAMES[type]}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {sectionButtons.length > 0 && (
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {sectionButtons.length}{' '}
                          {sectionButtons.length === 1 ? 'claim' : 'claims'}
                        </Typography>
                      )}
                      {sectionButtons.length > 0 && (
                        <Button
                          size="small"
                          variant="text"
                          disabled={movableSectionButtonCount === 0}
                          sx={{
                            color: 'primary.contrastText',
                            p: '2px 6px',
                            minWidth: 'auto',
                            fontSize: '0.7rem',
                            lineHeight: 1.2,
                            border: '1px solid rgba(255,255,255,0.5)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                            '&.Mui-disabled': {
                              color: 'rgba(255,255,255,0.72)',
                              WebkitTextFillColor: 'rgba(255,255,255,0.72)',
                              borderColor: 'rgba(255,255,255,0.35)',
                              opacity: 1,
                            },
                          }}
                          onClick={() => onAssignAll(type, quoteType)}
                        >
                          {quoteType === 'original' ? 'Assign All' : 'Remove All'}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns:
                            items[0]?.type === 'PT'
                              ? STEP2_PT_GRID_COLUMNS
                              : STEP2_STANDARD_GRID_COLUMNS,
                          gap: 1,
                          px: 1.5,
                          py: 0.5,
                          bgcolor: 'background.default',
                          borderBottom: '2px solid',
                          borderColor: 'primary.light',
                          mb: 0.5,
                          alignItems: 'center',
                          width: 'fit-content',
                          minWidth: '100%',
                        }}
                      >
                        {items[0]?.type === 'PT' ? (
                          <>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Provider
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                textAlign: 'right',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              # of Visits
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                textAlign: 'right',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Est. U&C/Visit
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                textAlign: 'right',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Est. U&C $
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                textAlign: 'right',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Quoted/Visit
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ textAlign: 'right' }}
                            >
                              Quoted $
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Provider
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Physician
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              CPT
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Description
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Cost Source
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                textAlign: 'right',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Charges $
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                textAlign: 'right',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                pr: 1,
                              }}
                            >
                              Quoted $
                            </Typography>
                          </>
                        )}
                      </Box>
                      {items.map((item) => (
                        <Paper
                          key={item.id}
                          elevation={1}
                          sx={{
                            mb: 0.5,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            width: 'fit-content',
                            minWidth: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns:
                                item.type === 'PT'
                                  ? STEP2_PT_GRID_COLUMNS
                                  : STEP2_STANDARD_GRID_COLUMNS,
                              gap: 1,
                              alignItems: 'center',
                              p: 1.5,
                            }}
                          >
                            {item.type === 'PT' ? (
                              <>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.providerGroup}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'right',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.numberOfVisits || 0}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'right',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  ${formatCurrency(item.estUCPerVisit || 0)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'right',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  ${formatCurrency(item.estUCTotal || 0)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'right',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  ${formatCurrency(item.quotedPerVisit || 0)}
                                </Typography>
                                <Typography variant="caption" sx={{ textAlign: 'right' }}>
                                  ${formatCurrency(item.quotedAmount)}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={item.providerGroup}
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.providerGroup}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={item.providerType}
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.providerType || '-'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.cptCode || '-'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={item.description}
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.description || '-'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  {item.costSource !== undefined
                                    ? CostSourceText[item.costSource]
                                    : '-'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'right',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    pr: 1,
                                  }}
                                >
                                  ${formatCurrency(item.billedCharges)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'right',
                                  }}
                                >
                                  ${formatCurrency(item.quotedAmount)}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  <Droppable droppableId={droppableId} direction="horizontal">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: 60,
                          bgcolor: snapshot.isDraggingOver
                            ? 'action.hover'
                            : 'background.default',
                          border: '2px dashed',
                          borderColor: snapshot.isDraggingOver
                            ? 'primary.main'
                            : 'divider',
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {sectionButtons.length === 0 ? (
                          <Box
                            sx={{
                              width: '100%',
                              p: 2,
                              textAlign: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            <Typography variant="caption">
                              {quoteType === 'consumer'
                                ? 'Drag claim buttons here'
                                : 'No claim buttons for this section'}
                            </Typography>
                          </Box>
                        ) : (
                          sectionButtons.map((button, index) => {
                            const isAppeal = button.appealTypeDescription !== undefined
                            const appealText = isAppeal
                              ? `[${button.appealTypeDescription}] `
                              : ''
                            const isLocked = !!button.originalBundleQuoteId

                            return (
                              <Draggable
                                key={button.id}
                                draggableId={button.id}
                                index={index}
                                isDragDisabled={isLocked}
                              >
                                {(provided, snapshot) => (
                                  <Tooltip
                                    title={
                                      <span>
                                        {button.percentageLineItemsReconciled !==
                                        undefined
                                          ? appealText +
                                            button.providerGroup +
                                            ' - ' +
                                            button.percentageLineItemsReconciled +
                                            '% Reconciled'
                                          : appealText + button.providerGroup}
                                        <br />
                                        DOS:{' '}
                                        {button.dateOfService
                                          ? button.dateOfService
                                          : '[UNKNOWN]'}
                                        {button.movedClaimInfo ? (
                                          <>
                                            <br />
                                            {button.movedClaimInfo}
                                          </>
                                        ) : null}
                                      </span>
                                    }
                                    disableInteractive
                                    disableHoverListener={snapshot.isDragging}
                                    disableFocusListener={snapshot.isDragging}
                                    disableTouchListener={snapshot.isDragging}
                                  >
                                    <Box
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      sx={{ display: 'inline-block', m: 1 }}
                                    >
                                      <Button
                                        component="div"
                                        sx={{
                                          ...(isAppeal
                                            ? {
                                                backgroundColor: 'error.main',
                                                color: 'error.contrastText',
                                                '&:hover': {
                                                  backgroundColor: 'error.dark',
                                                },
                                              }
                                            : {}),
                                          pointerEvents: snapshot.isDragging
                                            ? 'none'
                                            : 'auto',
                                        }}
                                        variant={
                                          button.percentageLineItemsReconciled === 100
                                            ? 'contained'
                                            : 'outlined'
                                        }
                                        size="small"
                                        color={
                                          button.originalBundleQuoteId
                                            ? 'inherit'
                                            : isAppeal
                                              ? undefined
                                              : 'primary'
                                        }
                                      >
                                        <Checkbox
                                          checked={quoteType === 'consumer'}
                                          disabled={isLocked}
                                          onChange={(e) =>
                                            onToggleButton(button.id, quoteType, e)
                                          }
                                          size="small"
                                          sx={{
                                            p: 0,
                                            mr: 0.5,
                                            color:
                                              isAppeal ||
                                              button.percentageLineItemsReconciled === 100
                                                ? 'white'
                                                : 'inherit',
                                            '&.Mui-checked': {
                                              color:
                                                isAppeal ||
                                                button.percentageLineItemsReconciled ===
                                                  100
                                                  ? 'white'
                                                  : 'primary.main',
                                            },
                                          }}
                                        />
                                        <Visibility fontSize="small" />
                                        &nbsp;{button.name}
                                      </Button>
                                    </Box>
                                  </Tooltip>
                                )}
                              </Draggable>
                            )
                          })
                        )}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              )
            })
          })()}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Card
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            py: 0.75,
            px: 1.5,
          }}
        >
          <CardContent sx={{ py: 0.75, px: 0, '&:last-child': { pb: 0.75 } }}>
            {quoteType === 'consumer' ? (
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Original Quoted Cost:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold' }}
                      color="text.primary"
                    >
                      ${formatCurrency(quote.totalOriginalCost)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Consumed Cost:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold' }}
                      color="text.primary"
                    >
                      ${formatCurrency(quote.consumedCost)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total After Consumed:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold' }}
                      color="text.primary"
                    >
                      ${formatCurrency(quote.consumedCost + quote.totalOriginalCost)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Charges:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold' }}
                  color="text.primary"
                >
                  ${formatCurrency(calculateTotal(quote.lineItems))}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
})

export default QuoteCardWithButtons
